import { describeAnchor, resolveAnchor, type TextAnchor } from '@app/studio'

/**
 * A passage's words, as the reader sees them, and the way back to them.
 *
 * Anchoring a remark to a phrase is two operations — reading a selection into
 * an anchor, and drawing a stored anchor back over the text — and they have to
 * measure the same string or a highlight lands beside the words it names. So
 * there is ONE projection of a block to text here, and both go through it.
 *
 * What it leaves out is everything that is not the author's words: a note's
 * number, a speaker's label, the mark in the margin. Those are computed by the
 * renderer rather than written, so a selection that crossed one would carry
 * characters the block does not hold and could never be checked against it.
 */

/** Marks a subtree as chrome — drawn from the book, not written in it. */
export const ANCHOR_SKIP = 'data-anchor-skip'

type Piece = { node: Text; from: number }
type Reading = { text: string; pieces: Piece[] }

/**
 * Every word in a block, with the text node each character came from.
 *
 * Block-level boundaries read as a newline — a verse's Hebrew and its
 * translation are two paragraphs on screen and two lines in the block — so the
 * projection matches what the block itself holds.
 */
export function readBlock(el: Element): Reading {
  const pieces: Piece[] = []
  let text = ''
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const value = node.nodeValue ?? ''
      if (!value) return
      pieces.push({ node: node as Text, from: text.length })
      text += value
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return
    const element = node as Element
    if (element.hasAttribute(ANCHOR_SKIP)) return
    const block = isBlockLevel(element)
    if (block && text && !text.endsWith('\n')) text += '\n'
    for (const child of Array.from(element.childNodes)) walk(child)
    if (block && text && !text.endsWith('\n')) text += '\n'
  }
  walk(el)
  return { text, pieces }
}

/** Whether an element ends the line it is on, for the projection above. */
function isBlockLevel(el: Element): boolean {
  const display = getComputedStyle(el).display
  return display !== 'inline' && display !== 'contents'
}

/**
 * The anchor a selection describes, if it is one.
 *
 * A selection has to lie inside ONE passage: a comment is about a passage, and
 * a range spanning two would head a thread with a block that holds half of what
 * it quotes. One that strays outside is refused rather than clamped — clamping
 * would silently store words nobody meant to select.
 */
export function anchorFromSelection(
  reader: Element,
  selection: Selection | null,
): { blockId: string; anchor: TextAnchor } | null {
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return null
  }
  const range = selection.getRangeAt(0)
  const block = blockOf(range.startContainer, reader)
  if (!block || block !== blockOf(range.endContainer, reader)) return null

  const reading = readBlock(block)
  const start = offsetOf(reading, range.startContainer, range.startOffset)
  const end = offsetOf(reading, range.endContainer, range.endOffset)
  if (start === null || end === null || end <= start) return null

  const anchor = describeAnchor(reading.text, start, end)
  if (!anchor.quote.trim()) return null
  const blockId = block.getAttribute('data-block')
  return blockId ? { blockId, anchor } : null
}

/** Where a stored anchor lands in this passage now, as a range to draw. */
export function rangeForAnchor(
  block: Element,
  anchor: TextAnchor,
): Range | null {
  const reading = readBlock(block)
  const found = resolveAnchor(reading.text, anchor)
  if (!found) return null
  const from = pointAt(reading, found.start)
  const to = pointAt(reading, found.end)
  if (!from || !to) return null
  const range = document.createRange()
  range.setStart(from.node, from.offset)
  range.setEnd(to.node, to.offset)
  return range
}

/** The passage a node sits in, within this reader. */
function blockOf(node: Node, reader: Element): Element | null {
  const from = node.nodeType === Node.ELEMENT_NODE ? node : node.parentNode
  const el = (from as Element | null)?.closest?.('[data-block]') ?? null
  return el && reader.contains(el) ? el : null
}

/** Where a DOM point falls in the projection, or null if it is not in it. */
function offsetOf(
  reading: Reading,
  container: Node,
  offset: number,
): number | null {
  if (container.nodeType === Node.TEXT_NODE) {
    const piece = reading.pieces.find((p) => p.node === container)
    return piece ? piece.from + offset : null
  }
  /* An element boundary — the selection starts or ends between children, so it
     takes the position of the text node it borders. */
  const child = container.childNodes[offset] ?? container.lastChild
  if (!child) return null
  const piece = reading.pieces.find((p) => child.contains(p.node))
  if (piece) return piece.from
  const last = [...reading.pieces]
    .reverse()
    .find((p) => container.contains(p.node))
  return last ? last.from + (last.node.nodeValue?.length ?? 0) : null
}

/** The text node and offset holding a position in the projection. */
function pointAt(
  reading: Reading,
  at: number,
): { node: Text; offset: number } | null {
  for (const piece of reading.pieces) {
    const length = piece.node.nodeValue?.length ?? 0
    if (at >= piece.from && at <= piece.from + length) {
      return { node: piece.node, offset: at - piece.from }
    }
  }
  return null
}
